import styles from './header-bar.module.scss';
import {
  AppBar, Avatar, AvatarLetterWhite, Container, Divider, Icon,
  IconButton, List, ListItem, ListItemAvatar, ListItemIcon,
  ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography
} from '@frontend-workspace/react-shared-lib';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GetRoles, TokenContext } from '../TokenContext';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { msalConfig } from '../msal-utils';

/* eslint-disable-next-line */
export interface HeaderBarProps {
  toggleDrawer: (event: any) => void;
}

export function HeaderBar(props: HeaderBarProps) {
  const { t, i18n } = useTranslation();
  const { instance } = useMsal();
  const userRoles = GetRoles();
  const rolesString = userRoles.join(", ");

  const token = useContext(TokenContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const logout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    });
  };

  const open = Boolean(anchorEl);

  const handleClick = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleLogout = () => logout();

  // Azure Static Web Apps' auto-generated hostnames are random adjective-noun-hash
  // strings ("ambitious-water-08e4cce03...") that don't contain any of the four
  // substrings above -- matched on the stable azurestaticapps.net domain instead, so
  // every SWA-hosted non-prod environment is covered without editing this per
  // resource. Stops matching once a tier's custom domain is cut over, which is the
  // correct moment for the banner to go away.
  const isTest = window.location.href.includes("test") || window.location.href.includes("dev") || window.location.href.includes("localhost") || window.location.href.includes("uat") || window.location.href.includes("azurestaticapps.net");

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('webkua-language', lang);
    location.reload();
  };

  useEffect(() => {
    const storedLanguage = localStorage.getItem('webkua-language') || 'no';
    i18n.changeLanguage(storedLanguage);
  }, [i18n]);

  return (
    <AppBar
      position="sticky"
      sx={{ height: 50, backgroundColor: isTest ? "#961818" : "#3D3B53" }}
      className="d-flex align-items-center shadow-none">
      <Container className='container-fluid container-fluid ps-0' maxWidth={false}>
        <Toolbar sx={{ height: 50, paddingRight: 0 }}>
          <Link className="me-3" to="/overview" aria-label={t("standard.overview") ?? ''}>
            <img src="../assets/gfx_ico_Sogelink_max.png" alt="" width="50px" height="50px" />
          </Link>
          <Typography
            variant="h2Toolbar"
            component="div"
            sx={{ flexGrow: 1 }}>
            <div className='d-none d-sm-block'>
              {`${isTest ? "TEST-" : ""}KUA`}
            </div>
          </Typography>

          {/* Language Flags */}
          <div className='d-flex align-items-center'>
            <Tooltip title="Norwegian">
              <IconButton onClick={() => changeLanguage('no')} className='me-3'>
                <img src="https://flagcdn.com/w40/no.png" alt="Norwegian" width="24" height="16" />
              </IconButton>
            </Tooltip>
            <Tooltip title="English">
              <IconButton onClick={() => changeLanguage('en')} className='me-3'>
                <img src="https://flagcdn.com/w40/gb.png" alt="English" width="24" height="16" />
              </IconButton>
            </Tooltip>
            <Tooltip title="French">
              <IconButton onClick={() => changeLanguage('fr')} className='me-3'>
                <img src="https://flagcdn.com/w40/fr.png" alt="French" width="24" height="16" />
              </IconButton>
            </Tooltip>
          </div>

          <span>
            <Tooltip arrow title={t("headerBar.yourUserSettings")}>
              <IconButton
                onClick={handleClick}
                color='secondary'
                className='p-0'>
                <AvatarLetterWhite sx={{ padding: 0, fontWeight: '600' }}>
                  {token.name}
                </AvatarLetterWhite>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
              <List sx={{ width: '100%', maxWidth: 360 }} className='pe-2'>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ background: 'transparent' }}>
                      <Icon
                        className='material-icons-outlined'
                        fontSize='large'
                        sx={{ color: '#4d4d4d' }}>
                        account_circle
                      </Icon>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<span className='MuiTypography-body2'>{token.name}</span>}
                    secondary={<span className='MuiTypography-body6'>{token.unique_name}</span>} />
                </ListItem>
                <ListItem>
                  <ListItemText>
                    <span className='MuiTypography-body6'>{t("headerBar.yourRoles")}</span>
                    <span className='MuiTypography-body2'>{rolesString}</span>
                  </ListItemText>
                </ListItem>
              </List>
              <Divider />
              <MenuItem
                onClick={handleLogout}>
                <ListItemIcon>
                  <Icon
                    fontSize='small'
                    sx={{ color: '#4d4d4d' }}>logout</Icon>
                </ListItemIcon>
                {t("headerBar.logOut")}
              </MenuItem>
            </Menu>
          </span>
          <IconButton
            edge="start"
            color="inherit"
            className="d-lg-none ms-3"
            onClick={props.toggleDrawer}>
            <Icon>menu</Icon>
          </IconButton>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default HeaderBar;